import React from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { keyframes } from '@compiled/react';

import { cssMap } from '@atlaskit/css';
import { Box } from '@atlaskit/primitives/compiled/box';
import SyncIcon from '@atlaskit/icon-lab/core/sync';
import { token } from '@atlaskit/tokens';

const i18n = defineMessages({
	syncingFolder: {
		id: 'smart-folder.sync-icon.syncing',
		defaultMessage: 'Syncing folder',
		description:
			'Accessible label for the animated sync icon shown while a smart folder is being synchronised.',
	},
});

const spinAnimation = keyframes({
	from: { transform: 'rotate(0deg)' },
	to: { transform: 'rotate(360deg)' },
});

const styles = cssMap({
	root: {
		display: 'inline-flex',
		alignItems: 'center',
		justifyContent: 'center',
		animationName: spinAnimation,
		animationDuration: '1.4s',
		animationIterationCount: 'infinite',
		animationTimingFunction: 'linear',
	},
});

/**
 * A small animated sync icon shown while a smart folder is being synchronised.
 *
 * Renders a rotating `SyncIcon` wrapped in an `inline-flex` container.
 * The animation is applied to the wrapper (not the SVG itself) so that React
 * re-renders of the SVG do not reset the spin timeline.
 */
export const SmartFolderSyncIcon = ({ testId }: { testId?: string }): React.JSX.Element => {
	const intl = useIntl();
	const label = intl.formatMessage(i18n.syncingFolder);
	return (
		<Box as="span" xcss={styles.root} testId={testId} role="status" aria-label={label}>
			<SyncIcon label="" color={token('color.icon.subtle')} />
		</Box>
	);
};
